<template>
  <v-navigation-drawer v-model="drawer" clipped app>
    <v-list dense nav>
      <v-subheader v-if="validSites">{{ $t('message.sites') }}</v-subheader>
      <div v-if="isLoading" class="pl-3">
        <v-progress-circular size="24" indeterminate color="primary"></v-progress-circular>
      </div>
      <template v-if="validSites">
        <template v-for="site in validSites">
          <v-list-group :key="site['@id']" :value="true" color="black">
            <template v-slot:activator>
              <v-list-item-avatar v-if="site.image" size="25" style="margin-right: 30px !important;">
                <img :src="site.image.contentUrl" :alt="site.name" />
              </v-list-item-avatar>
              <v-list-item-icon v-else>
                <v-icon>{{ site['@type'] === 'Vessel' ? 'mdi-ferry' : 'mdi-satellite-uplink' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ site.name }}
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="agreement in site.connectAgreements"
              :key="agreement['@id']"
              link
              :to="{
                name: 'AgreementShow',
                params: { id: $iriToId(agreement['@id']) }
              }"
              :disabled="!agreement.startsAt"
            >
              <v-list-item-title
                style="margin-left: 56px"
                v-text="agreement.alias ? agreement.alias : agreement.servicePlan.name"
              ></v-list-item-title>
              <v-list-item-icon>
                <v-icon
                  :color="!agreement.startsAt ? 'grey lighten-2' : agreement.contractuallyActivated ? 'green' : 'red'"
                  >{{ getTechnologyIcon(agreement.servicePlan.technology) }}</v-icon
                >
              </v-list-item-icon>
            </v-list-item>
            <v-list-item
              v-if="site.siteVpns.length > 0"
              :to="{
                name: 'VpnShow',
                params: { id: $iriToId(site['@id']) }
              }"
            >
              <v-list-item-title style="margin-left: 56px">SD-WAN</v-list-item-title>
              <v-list-item-icon>
                <v-icon>mdi-security</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list-group>
        </template>
      </template>

      <v-divider v-if="validSites" dark class="my-3"></v-divider>
      <v-list-item exact :to="{ name: 'orders' }">
        <v-list-item-action>
          <v-icon>mdi-cart</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('message.shopping_history') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="$store.state.drawer.showPaymentMethods" exact :to="{ name: 'paymentMethods' }">
        <v-list-item-action>
          <v-icon>mdi-credit-card</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('message.payment_methods.title') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item exact :to="{ name: 'support' }">
        <v-list-item-action>
          <v-icon>mdi-lifebuoy</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('message.help.title') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fetch from '@/utils/fetch'

export default {
  name: 'NavigationDrawer',

  data() {
    return {
      legalEntities: null
    }
  },

  computed: {
    drawer: {
      get() {
        return this.$store.state.drawer.status
      },
      set(val) {
        this.$store.commit('drawerSetStatus', val)
      }
    },
    ...mapGetters({
      error: 'connectagreement/list/error',
      agreements: 'connectagreement/list/items',
      isLoading: 'connectagreement/list/isLoading',
      view: 'connectagreement/list/view'
    }),

    validSites() {
      let sites = []

      if (this.agreements) {
        let agreements = this.agreements.filter(agreement => {
          return (
            null === agreement.startsAt ||
            (this.$moment() > this.$moment(agreement.startsAt) &&
              (!agreement.endsAt || this.$moment() < this.$moment(agreement.endsAt)))
          )
        })

        agreements.forEach(agreement => {
          let exists = false
          sites.forEach(site => {
            if (agreement.site['@id'] === site['@id']) {
              exists = true
            }
          })
          if (exists === false) {
            sites.push(agreement.site)
          }
        })
        agreements.forEach(agreement => {
          sites.forEach(site => {
            if (site['@id'] === agreement.site['@id']) {
              if (!('connectAgreements' in site)) {
                site.connectAgreements = []
              }

              site.connectAgreements.push(agreement)
            }
          })
        })
      }

      return sites.length > 0 ? sites : null
    },

    hasStripeCustomer() {
      if (!this.legalEntities) {
        return false
      }
      return (
        this.legalEntities.filter(legalEntity => {
          return !!legalEntity.stripeCustomerId
        }).length > 0
      )
    }
  },

  watch: {
    hasStripeCustomer(newValue) {
      this.$store.commit('drawerSetShowPaymentMethods', newValue)
    }
  },

  mounted() {
    this.getAgreements('/connect_agreements?pagination=false&archived=false')
    if (null === this.$store.state.drawer.showPaymentMethods) {
      this.getLegalEntities()
    }
  },

  methods: {
    ...mapActions({
      getAgreements: 'connectagreement/list/default'
    }),

    getTechnologyIcon(technology) {
      switch (technology) {
        case 'vsat':
        case 'l_band':
          return 'mdi-satellite-variant'
        case '3g_lte':
          return 'mdi-antenna'
        default:
          return 'mdi-circle'
      }
    },

    getsiteValidAgreements(site) {
      let currentAgreements = []

      if (site.connectAgreements.length > 0) {
        site.connectAgreements.forEach(agreement => {
          if (agreement.contractuallyActivated) {
            currentAgreements.push(agreement)
          }
        })
      }

      return currentAgreements
    },

    getLegalEntities() {
      fetch('/legal_entities')
        .then(response => response.json())
        .then(data => {
          this.legalEntities = data['hydra:member']
        })
    }
  }
}
</script>
